// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { observer } from "mobx-react-lite";
import { ComponentType, FC } from "react";

import { hydrationFailure } from "../Hydratable/utils";
import { Hydrator } from "./Hydrator";
import { HydratorProps } from "./types";

type HydratorWithoutErrorLoggingProps = Omit<HydratorProps, "failed"> & {
  // similar API as HydratorWithErrorLogging for convenience
  fallback: ComponentType<{ error: Error }>;
};

/**
 * Catches and displays hydration error messages using the provided fallback component,
 * but does not log them to Sentry
 */
export const HydratorWithoutErrorLogging: FC<HydratorWithoutErrorLoggingProps> =
  observer(function HydratorWithoutErrorLogging(hydratorProps) {
    const error = hydrationFailure(hydratorProps.hydratable);
    if (error) {
      return <hydratorProps.fallback error={error} />;
    }

    return (
      <Hydrator
        {...hydratorProps}
        // this should never be seen because the parent component will render an error message first
        failed={null}
      />
    );
  });
